import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge, Button, Group, Paper, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconEye, IconRefresh, IconTrash } from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { api } from 'src/common/utils/api';
import { AdminBackgroundProcess } from 'src/secret-menu/background-processes/types';
import { asUtcDate, getStatusColor } from 'src/secret-menu/background-processes/display';
import { BackgroundProcessDetailModal } from 'src/secret-menu/background-processes/BackgroundProcessDetailModal';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CALENDAR_ORPHAN_SWEEP_PROCESS_NAME, useCalendarSweepStatus } from './useCalendarSweep';

type CalendarSweepPanelProps = {
  company: Company;
};

const RUN_COLUMNS: DataTableColumn<AdminBackgroundProcess>[] = [
  {
    accessor: 'updatedAt',
    title: 'Finished',
    width: 180,
    sortable: true,
    render: (record: AdminBackgroundProcess) => (
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
        {asUtcDate(record.updatedAt).toLocaleString()}
      </Text>
    ),
  },
  {
    accessor: 'status',
    title: 'Status',
    width: 120,
    render: (record: AdminBackgroundProcess) => (
      <Badge color={getStatusColor(record.status)} variant="light">
        {record.status}
      </Badge>
    ),
  },
  {
    accessor: 'statusMessage',
    title: 'Outcome',
    ellipsis: true,
    render: (record: AdminBackgroundProcess) => (
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10" truncate="end">
        {record.statusMessage}
      </Text>
    ),
  },
];

type LastRunBadge = { color: string; label: string; tooltip: string };

/**
 * A run only reaches a terminal status once it has recorded its figures, so anything else on the
 * last run has to be said out loud. A crashed run is left non-terminal with no swept count, which
 * would otherwise read as a healthy run that removed nothing.
 */
function lastRunBadge(
  lastRunStatus: AdminBackgroundProcess['status'] | null,
  runInProgress: boolean,
): LastRunBadge | null {
  if (lastRunStatus === 'FAILED') {
    return {
      color: 'red',
      label: 'Failed',
      tooltip:
        'The last run stopped with an error. Open it in the table below to see what stopped it.',
    };
  }
  if (runInProgress) {
    return {
      color: 'blue',
      label: 'Running',
      tooltip: 'This run is still going, so the triggers stay disabled until it finishes.',
    };
  }
  if (lastRunStatus === 'PENDING' || lastRunStatus === 'PROCESSING') {
    return {
      color: 'orange',
      label: 'Did not finish',
      tooltip:
        'The last run stopped without recording an outcome, so its figures are missing. Nothing is holding up a new run.',
    };
  }
  return null;
}

export function CalendarSweepPanel({ company }: CalendarSweepPanelProps) {
  const { status, isError, error, refetch, isRefetching } = useCalendarSweepStatus(company.id);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  useEffect(() => {
    if (isError && error) {
      notifyAndLogError('Calendar sweep status failed', 'Could not load the sweep status', error, {
        companyId: company.id,
      });
    }
  }, [isError, error, company.id]);

  // Deliberately does not invalidate the status query: the endpoint returns as soon as the run is
  // queued, so refetching straight away would show the figures from before it started.
  // mutate rather than mutateAsync: nothing consumes the resolved value, and mutateAsync rejects
  // on failure even with onError defined, which surfaces as an unhandled rejection.
  const { mutate: triggerSweep, isPending } = useMutation<unknown, Error, 'preview' | 'execute'>({
    mutationFn: action => api.post(`admin/companies/${company.id}/calendar-sweep/${action}`),
    onSuccess: (_data, action) =>
      notifications.show({
        title: action === 'preview' ? 'Preview started' : 'Sweep started',
        message: 'It runs in the background — refresh in a moment to see the result.',
      }),
    onError: (mutationError, action) =>
      notifyAndLogError(
        'Calendar sweep failed',
        action === 'preview' ? 'Could not start the preview' : 'Could not start the sweep',
        mutationError,
      ),
  });

  const confirmRun = () => {
    closeConfirm();
    triggerSweep('execute');
  };

  // A destructive trigger is not offered while the panel cannot state its blast radius, nor on top
  // of a run already holding the tenant — the endpoint refuses that with a 409 either way.
  const triggersDisabled = !status || status.runInProgress;
  const badge = status ? lastRunBadge(status.lastRunStatus, status.runInProgress) : null;

  return (
    <Paper p="md" withBorder>
      <BackgroundProcessDetailModal
        processId={selectedRunId}
        companyId={company.id}
        onClose={() => setSelectedRunId(null)}
      />

      <Stack gap="md">
        <Group justify="space-between">
          <Stack gap={0}>
            <Title order={4}>Superseded calendar sweep</Title>
            <Text size="xs" c="dimmed">
              {company.name} ({company.tenantName})
            </Text>
          </Stack>
          <Group gap="xs">
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconRefresh size={16} />}
              loading={isRefetching}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconEye size={16} />}
              loading={isPending}
              disabled={triggersDisabled}
              onClick={() => triggerSweep('preview')}
            >
              Preview
            </Button>
            <Button
              size="compact-sm"
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} />}
              loading={isPending}
              disabled={triggersDisabled}
              onClick={openConfirm}
            >
              Run sweep
            </Button>
          </Group>
        </Group>

        <Text size="sm" c="dimmed">
          A reconnect leaves the previous calendar alive at Recall, so it keeps sending sync events
          for a calendar this firm no longer uses. The sweep deletes those, but only once they have
          been superseded for a day and the adviser has a working calendar again.
        </Text>

        {status && (
          <Group gap="xl">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Awaiting sweep
              </Text>
              <Text size="xl" fw={FONT_WEIGHTS.semibold}>
                {status.awaitingSweep}
              </Text>
            </Stack>

            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Last run
              </Text>
              <Group gap="xs">
                <Text size="sm" fw={FONT_WEIGHTS.medium}>
                  {status.lastRunAt ? new Date(status.lastRunAt).toLocaleString() : 'Never run'}
                </Text>
                {badge && (
                  <Tooltip label={badge.tooltip}>
                    <Badge color={badge.color} variant="light" size="sm">
                      {badge.label}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Stack>

            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Removed by the last run
              </Text>
              <Group gap="xs">
                <Text size="sm" fw={FONT_WEIGHTS.medium}>
                  {status.lastRunSwept ?? '—'}
                </Text>
                {status.lastRunWasDryRun && (
                  <Tooltip label="The last run was a preview, so nothing was deleted. Either it was started as a preview, or the calendar orphan sweep flag is off for this firm.">
                    <Badge color="yellow" variant="light" size="sm">
                      Preview only
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Stack>

            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Needs a human
              </Text>
              <Group gap="xs">
                <Tooltip label="Soft-deleted, still live at Recall, but no connected calendar shares the address — so we cannot prove the adviser has working sync. Use the per-calendar actions below.">
                  <Badge
                    color={status.excludedWithoutLiveSuccessor ? 'orange' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {status.excludedWithoutLiveSuccessor} without a successor
                  </Badge>
                </Tooltip>
                <Tooltip label="Soft-deleted with no email address recorded, so there is nothing to match a replacement calendar against.">
                  <Badge
                    color={status.excludedWithoutEmailAddress ? 'orange' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {status.excludedWithoutEmailAddress} without an address
                  </Badge>
                </Tooltip>
              </Group>
            </Stack>
          </Group>
        )}

        {!status && isError && (
          <Group gap="sm">
            <Text size="sm" c="red">
              Could not load the sweep status, so there is no way to tell how many calendars a run
              would delete. Nothing can be started until it loads.
            </Text>
            <Button
              size="compact-sm"
              variant="light"
              loading={isRefetching}
              onClick={() => refetch()}
            >
              Try again
            </Button>
          </Group>
        )}

        {!status && !isError && (
          <Text size="sm" c="dimmed">
            Loading sweep status…
          </Text>
        )}

        <SimpleTable<AdminBackgroundProcess>
          mode="server"
          resource={`admin/companies/${company.id}/background-processes`}
          filters={{ processName: CALENDAR_ORPHAN_SWEEP_PROCESS_NAME }}
          title="Previous runs"
          columns={RUN_COLUMNS}
          initialSort={{ columnAccessor: 'updatedAt', direction: 'desc' }}
          onRowClick={(record: AdminBackgroundProcess) => setSelectedRunId(record.id)}
          showCount
        />
      </Stack>

      {status && (
        <ConfirmDeleteModal
          opened={confirmOpened}
          onClose={closeConfirm}
          onConfirm={confirmRun}
          title="Run the superseded calendar sweep"
          message={`Delete up to ${status.awaitingSweep} superseded calendars from Recall for ${company.name} (${company.tenantName})? Nothing is deleted unless the calendar orphan sweep flag is on for this firm.`}
          confirmLabel="Run sweep"
          isLoading={isPending}
        />
      )}
    </Paper>
  );
}
